/**
 * Единый холст для черепахи / робота / мини-игр.
 * Слушает CustomEvent('diobuch-canvas') (и устаревший diobuch-turtle).
 * Клики в режиме session шлёт обратно через onCanvasEvent / CustomEvent('diobuch-canvas-input').
 */
import { useEffect, useRef, useState } from 'react';

const DEFAULT_SIZE = 360;
const EVENT_IN = 'diobuch-canvas';
const EVENT_IN_LEGACY = 'diobuch-turtle';
const EVENT_OUT = 'diobuch-canvas-input';

const SPEED_MS = {
  0: 0,
  1: 80,
  2: 50,
  3: 35,
  4: 25,
  5: 18,
  6: 12,
  7: 8,
  8: 5,
  9: 3,
  10: 0,
};

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function wallKey(r, c, side) {
  return `${r},${c},${side}`;
}

/** Нормализация: стена на севере клетки (r,c) = юг клетки (r-1,c). */
function normalizeWall(r, c, side) {
  const s = String(side || '').toUpperCase();
  if (s === 'N' || s === 'NORTH' || s === 'UP' || s === 'С') return wallKey(r, c, 'N');
  if (s === 'S' || s === 'SOUTH' || s === 'DOWN' || s === 'Ю') return wallKey(r + 1, c, 'N');
  if (s === 'W' || s === 'WEST' || s === 'LEFT' || s === 'З') return wallKey(r, c, 'W');
  if (s === 'E' || s === 'EAST' || s === 'RIGHT' || s === 'В') return wallKey(r, c + 1, 'W');
  return wallKey(r, c, s);
}

function hasWall(walls, r, c, side) {
  const s = String(side || '').toUpperCase();
  if (s === 'N' || s === 'UP') return walls.has(wallKey(r, c, 'N'));
  if (s === 'S' || s === 'DOWN') return walls.has(wallKey(r + 1, c, 'N'));
  if (s === 'W' || s === 'LEFT') return walls.has(wallKey(r, c, 'W'));
  if (s === 'E' || s === 'RIGHT') return walls.has(wallKey(r, c + 1, 'W'));
  return false;
}

export default function CodeCanvas({
  height = DEFAULT_SIZE,
  mode: initialMode = 'turtle',
  config = {},
  onCanvasEvent = null,
  title = null,
}) {
  const canvasRef = useRef(null);
  const [hint, setHint] = useState(() => hintForMode(initialMode));
  const [modeLabel, setModeLabel] = useState(initialMode);
  const stateRef = useRef(null);
  const queueRef = useRef([]);
  const processingRef = useRef(false);
  const tickTimerRef = useRef(null);
  const afterTimersRef = useRef(new Map());
  const onCanvasEventRef = useRef(onCanvasEvent);
  onCanvasEventRef.current = onCanvasEvent;
  const configKey = JSON.stringify(config && typeof config === 'object' ? config : {});

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth || DEFAULT_SIZE;
    const h = height;
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    let cfg = {};
    try {
      cfg = JSON.parse(configKey);
    } catch {
      cfg = {};
    }
    const robotRows = Number(cfg.rows) > 0 ? Number(cfg.rows) : 8;
    const robotCols = Number(cfg.cols) > 0 ? Number(cfg.cols) : 8;
    const start = Array.isArray(cfg.start) ? cfg.start : [0, 0];
    const walls = new Set();
    if (Array.isArray(cfg.walls)) {
      for (const item of cfg.walls) {
        if (Array.isArray(item) && item.length >= 3) {
          walls.add(normalizeWall(Number(item[0]), Number(item[1]), item[2]));
        } else if (item && typeof item === 'object') {
          walls.add(normalizeWall(Number(item.r ?? item.row), Number(item.c ?? item.col), item.side || item.dir));
        }
      }
    }
    const painted = new Set();
    if (Array.isArray(cfg.painted)) {
      for (const p of cfg.painted) {
        if (Array.isArray(p)) painted.add(`${Number(p[0])},${Number(p[1])}`);
        else if (p && typeof p === 'object') painted.add(`${Number(p.r ?? p.row)},${Number(p.c ?? p.col)}`);
      }
    }

    const state = {
      mode: initialMode === 'robot' || initialMode === 'game' ? initialMode : 'turtle',
      w,
      h,
      // turtle (math coords: origin center, +y up, heading 0 = east)
      x: 0,
      y: 0,
      angle: 0,
      penDown: true,
      color: '#7dd3fc',
      fillColor: null,
      filling: false,
      fillPath: [],
      showTurtle: true,
      showGrid: initialMode === 'turtle',
      gridStep: Number(cfg.grid_step) > 0 ? Number(cfg.grid_step) : 20,
      speed: 6,
      shapes: new Map(),
      sceneObjects: new Map(),
      nextShapeId: 1,
      session: false,
      // robot
      robotRows,
      robotCols,
      robotRow: Number(start[0]) || 0,
      robotCol: Number(start[1]) || 0,
      robotFacing: String(start[2] || 'E').toUpperCase().replace('EAST', 'E').replace('RIGHT', 'E'),
      walls,
      painted,
      robotMsg: '',
    };
    stateRef.current = state;
    setModeLabel(state.mode);
    setHint(hintForMode(state.mode));

    function toCanvas(x, y) {
      return { cx: state.w / 2 + x, cy: state.h / 2 - y };
    }

    function redrawBackground() {
      ctx.fillStyle = '#0f1419';
      ctx.fillRect(0, 0, state.w, state.h);
      if (state.mode === 'turtle' && state.showGrid) {
        drawMathGrid();
      } else if (state.mode === 'robot') {
        drawRobotField();
      }
    }

    function drawMathGrid() {
      const step = state.gridStep;
      const { w: ww, h: hh } = state;
      ctx.save();
      ctx.strokeStyle = 'rgba(148, 163, 184, 0.25)';
      ctx.lineWidth = 1;
      const ox = ww / 2;
      const oy = hh / 2;
      for (let x = ox % step; x < ww; x += step) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, hh);
        ctx.stroke();
      }
      for (let y = oy % step; y < hh; y += step) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(ww, y);
        ctx.stroke();
      }
      ctx.strokeStyle = 'rgba(148, 163, 184, 0.55)';
      ctx.beginPath();
      ctx.moveTo(0, oy);
      ctx.lineTo(ww, oy);
      ctx.moveTo(ox, 0);
      ctx.lineTo(ox, hh);
      ctx.stroke();
      ctx.restore();
    }

    function cellSize() {
      const pad = 8;
      return Math.min(
        (state.w - pad * 2) / state.robotCols,
        (state.h - pad * 2) / state.robotRows,
      );
    }

    function cellOrigin() {
      const cs = cellSize();
      return {
        ox: (state.w - cs * state.robotCols) / 2,
        oy: (state.h - cs * state.robotRows) / 2,
        cs,
      };
    }

    function drawRobotField() {
      const { ox, oy, cs } = cellOrigin();
      for (let r = 0; r < state.robotRows; r += 1) {
        for (let c = 0; c < state.robotCols; c += 1) {
          const x = ox + c * cs;
          const y = oy + r * cs;
          ctx.fillStyle = state.painted.has(`${r},${c}`) ? 'rgba(56, 189, 248, 0.35)' : '#1a2332';
          ctx.fillRect(x, y, cs, cs);
          ctx.strokeStyle = 'rgba(148, 163, 184, 0.35)';
          ctx.strokeRect(x, y, cs, cs);
        }
      }
      // walls
      ctx.strokeStyle = '#f87171';
      ctx.lineWidth = 3;
      for (const key of state.walls) {
        const [rs, csStr, side] = key.split(',');
        const r = Number(rs);
        const c = Number(csStr);
        if (side === 'N') {
          const x1 = ox + c * cs;
          const y1 = oy + r * cs;
          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(x1 + cs, y1);
          ctx.stroke();
        } else if (side === 'W') {
          const x1 = ox + c * cs;
          const y1 = oy + r * cs;
          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(x1, y1 + cs);
          ctx.stroke();
        }
      }
      // outer border thicker
      ctx.strokeStyle = '#94a3b8';
      ctx.lineWidth = 2;
      ctx.strokeRect(ox, oy, cs * state.robotCols, cs * state.robotRows);
      drawRobotSprite();
      if (state.robotMsg) {
        ctx.fillStyle = '#fbbf24';
        ctx.font = '12px sans-serif';
        ctx.fillText(state.robotMsg, 8, state.h - 8);
      }
    }

    function drawRobotSprite() {
      const { ox, oy, cs } = cellOrigin();
      const cx = ox + (state.robotCol + 0.5) * cs;
      const cy = oy + (state.robotRow + 0.5) * cs;
      const facing = state.robotFacing;
      let rot = 0;
      if (facing === 'E' || facing === 'RIGHT') rot = 0;
      else if (facing === 'S' || facing === 'DOWN') rot = Math.PI / 2;
      else if (facing === 'W' || facing === 'LEFT') rot = Math.PI;
      else if (facing === 'N' || facing === 'UP') rot = -Math.PI / 2;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(rot);
      ctx.fillStyle = '#38bdf8';
      ctx.beginPath();
      ctx.moveTo(cs * 0.32, 0);
      ctx.lineTo(-cs * 0.28, -cs * 0.22);
      ctx.lineTo(-cs * 0.28, cs * 0.22);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    function drawTurtleIcon() {
      if (!state.showTurtle || state.mode !== 'turtle') return;
      const { cx, cy } = toCanvas(state.x, state.y);
      const rad = (-state.angle * Math.PI) / 180;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(rad);
      ctx.fillStyle = state.color || '#7dd3fc';
      ctx.strokeStyle = '#0f1419';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(10, 0);
      ctx.lineTo(-7, -6);
      ctx.lineTo(-4, 0);
      ctx.lineTo(-7, 6);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    }

    let redrawScheduled = false;
    function scheduleRedraw() {
      if (redrawScheduled) return;
      redrawScheduled = true;
      requestAnimationFrame(() => {
        redrawScheduled = false;
        redrawAll();
      });
    }

    function redrawAll() {
      redrawBackground();
      if (state.mode === 'game') {
        for (const obj of state.sceneObjects.values()) {
          paintSceneObject(obj);
        }
        for (const shape of state.shapes.values()) {
          paintShape(shape);
        }
        return;
      }
      if (state.mode === 'turtle') {
        for (const shape of state.shapes.values()) {
          paintShape(shape);
        }
        for (const seg of state.trail || []) {
          ctx.beginPath();
          ctx.strokeStyle = seg.color;
          ctx.lineWidth = seg.width || 2;
          ctx.moveTo(seg.x1, seg.y1);
          ctx.lineTo(seg.x2, seg.y2);
          ctx.stroke();
        }
        for (const fill of state.fills || []) {
          ctx.beginPath();
          ctx.fillStyle = fill.color;
          const pts = fill.points;
          if (!pts.length) continue;
          const p0 = toCanvas(pts[0].x, pts[0].y);
          ctx.moveTo(p0.cx, p0.cy);
          for (let i = 1; i < pts.length; i += 1) {
            const p = toCanvas(pts[i].x, pts[i].y);
            ctx.lineTo(p.cx, p.cy);
          }
          ctx.closePath();
          ctx.fill();
        }
        drawTurtleIcon();
      }
    }

    state.trail = [];
    state.fills = [];

    function paintSceneObject(obj) {
      if (!obj) return;
      ctx.save();
      const fill = obj.fill || '#38bdf8';
      if (obj.kind === 'circle') {
        const r = Math.max(0.5, Number(obj.r) || 0);
        ctx.beginPath();
        ctx.fillStyle = fill;
        ctx.arc(Number(obj.x) || 0, Number(obj.y) || 0, r, 0, Math.PI * 2);
        ctx.fill();
      } else if (obj.kind === 'rect') {
        const w = Number(obj.w) || 0;
        const h = Number(obj.h) || 0;
        ctx.fillStyle = fill;
        ctx.fillRect(Number(obj.x) || 0, Number(obj.y) || 0, w, h);
      }
      ctx.restore();
    }

    function paintShape(shape) {
      if (!shape) return;
      ctx.save();
      if (shape.type === 'rect') {
        ctx.fillStyle = shape.fill || 'transparent';
        ctx.strokeStyle = shape.outline || shape.fill || '#7dd3fc';
        ctx.lineWidth = 2;
        const x = Math.min(shape.x1, shape.x2);
        const y = Math.min(shape.y1, shape.y2);
        const ww = Math.abs(shape.x2 - shape.x1);
        const hh = Math.abs(shape.y2 - shape.y1);
        if (shape.fill) ctx.fillRect(x, y, ww, hh);
        ctx.strokeRect(x, y, ww, hh);
      } else if (shape.type === 'oval') {
        const x = Math.min(shape.x1, shape.x2);
        const y = Math.min(shape.y1, shape.y2);
        const ww = Math.abs(shape.x2 - shape.x1);
        const hh = Math.abs(shape.y2 - shape.y1);
        ctx.beginPath();
        ctx.ellipse(x + ww / 2, y + hh / 2, ww / 2, hh / 2, 0, 0, Math.PI * 2);
        if (shape.fill) {
          ctx.fillStyle = shape.fill;
          ctx.fill();
        }
        ctx.strokeStyle = shape.outline || shape.fill || '#7dd3fc';
        ctx.lineWidth = 2;
        ctx.stroke();
      } else if (shape.type === 'line') {
        ctx.beginPath();
        ctx.strokeStyle = shape.fill || '#7dd3fc';
        ctx.lineWidth = 2;
        ctx.moveTo(shape.x1, shape.y1);
        ctx.lineTo(shape.x2, shape.y2);
        ctx.stroke();
      } else if (shape.type === 'text') {
        ctx.fillStyle = shape.fill || '#e2e8f0';
        ctx.font = shape.font || '14px sans-serif';
        ctx.fillText(shape.text || '', shape.x, shape.y);
      } else if (shape.type === 'poly' && Array.isArray(shape.points)) {
        const pts = shape.points;
        if (pts.length) {
          ctx.beginPath();
          ctx.moveTo(pts[0][0], pts[0][1]);
          for (let i = 1; i < pts.length; i += 1) ctx.lineTo(pts[i][0], pts[i][1]);
          ctx.closePath();
          if (shape.fill) {
            ctx.fillStyle = shape.fill;
            ctx.fill();
          }
          ctx.strokeStyle = shape.outline || shape.fill || '#7dd3fc';
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      }
      ctx.restore();
    }

    function clearAll() {
      state.x = 0;
      state.y = 0;
      state.angle = 0;
      state.penDown = true;
      state.color = '#7dd3fc';
      state.filling = false;
      state.fillPath = [];
      state.trail = [];
      state.fills = [];
      state.shapes.clear();
      state.sceneObjects.clear();
      state.robotMsg = '';
      if (Array.isArray(cfg.painted)) {
        state.painted = new Set();
        for (const p of cfg.painted) {
          if (Array.isArray(p)) state.painted.add(`${Number(p[0])},${Number(p[1])}`);
        }
      } else {
        state.painted = new Set();
      }
      state.robotRow = Number(start[0]) || 0;
      state.robotCol = Number(start[1]) || 0;
      redrawAll();
    }

    function drawLineMath(x1, y1, x2, y2) {
      if (!state.penDown) return;
      const a = toCanvas(x1, y1);
      const b = toCanvas(x2, y2);
      state.trail.push({
        x1: a.cx, y1: a.cy, x2: b.cx, y2: b.cy, color: state.color, width: 2,
      });
      ctx.beginPath();
      ctx.strokeStyle = state.color;
      ctx.lineWidth = 2;
      ctx.moveTo(a.cx, a.cy);
      ctx.lineTo(b.cx, b.cy);
      ctx.stroke();
    }

    function moveTurtleTo(nx, ny) {
      if (state.filling) state.fillPath.push({ x: nx, y: ny });
      drawLineMath(state.x, state.y, nx, ny);
      state.x = nx;
      state.y = ny;
      redrawAll();
    }

    function emitOut(event) {
      const handler = onCanvasEventRef.current;
      if (typeof handler === 'function') handler(event);
      else if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(EVENT_OUT, { detail: event }));
      }
    }

    function handleCommand(cmd) {
      if (!cmd || typeof cmd !== 'object') return;
      const type = String(cmd.type || '').toLowerCase();
      const s = state;

      if (type === 'clear' || type === 'reset') {
        clearAll();
        return;
      }
      if (type === 'mode' && cmd.mode) {
        s.mode = String(cmd.mode);
        s.showGrid = s.mode === 'turtle';
        setModeLabel(s.mode);
        setHint(hintForMode(s.mode));
        clearAll();
        return;
      }
      if (type === 'grid') {
        if (cmd.show != null) s.showGrid = !!cmd.show;
        if (Number.isFinite(Number(cmd.step))) s.gridStep = Number(cmd.step);
        redrawAll();
        return;
      }
      if (type === 'speed') {
        const v = Number(cmd.value);
        if (Number.isFinite(v)) s.speed = Math.max(0, Math.min(10, Math.round(v)));
        return;
      }
      if (type === 'session') {
        s.session = !!cmd.active;
        return;
      }
      if (type === 'pendown') { s.penDown = true; return; }
      if (type === 'penup') { s.penDown = false; return; }
      if (type === 'color' && cmd.color) {
        s.color = String(cmd.color);
        if (cmd.fill) s.fillColor = String(cmd.fill);
        return;
      }
      if (type === 'showturtle') { s.showTurtle = true; redrawAll(); return; }
      if (type === 'hideturtle') { s.showTurtle = false; redrawAll(); return; }
      if (type === 'setheading' && Number.isFinite(Number(cmd.angle))) {
        s.angle = Number(cmd.angle);
        redrawAll();
        return;
      }
      if (type === 'goto') {
        const nx = Number(cmd.x);
        const ny = Number(cmd.y);
        if (Number.isFinite(nx) && Number.isFinite(ny)) moveTurtleTo(nx, ny);
        return;
      }
      if (type === 'setx' && Number.isFinite(Number(cmd.x))) {
        moveTurtleTo(Number(cmd.x), s.y);
        return;
      }
      if (type === 'sety' && Number.isFinite(Number(cmd.y))) {
        moveTurtleTo(s.x, Number(cmd.y));
        return;
      }
      if (type === 'forward') {
        const dist = Number(cmd.distance) || 0;
        const rad = (s.angle * Math.PI) / 180;
        moveTurtleTo(s.x + Math.cos(rad) * dist, s.y + Math.sin(rad) * dist);
        return;
      }
      if (type === 'left') {
        s.angle += Number(cmd.angle) || 0;
        redrawAll();
        return;
      }
      if (type === 'right') {
        s.angle -= Number(cmd.angle) || 0;
        redrawAll();
        return;
      }
      if (type === 'begin_fill') {
        s.filling = true;
        s.fillPath = [{ x: s.x, y: s.y }];
        s.fillColor = cmd.color ? String(cmd.color) : s.color;
        return;
      }
      if (type === 'end_fill') {
        if (s.filling && s.fillPath.length >= 3) {
          s.fills.push({ points: [...s.fillPath], color: s.fillColor || s.color });
        }
        s.filling = false;
        s.fillPath = [];
        redrawAll();
        return;
      }
      if (type === 'dot') {
        const size = Number(cmd.size) || 4;
        const { cx, cy } = toCanvas(s.x, s.y);
        ctx.beginPath();
        ctx.fillStyle = cmd.color ? String(cmd.color) : s.color;
        ctx.arc(cx, cy, size, 0, Math.PI * 2);
        ctx.fill();
        return;
      }
      if (type === 'circle') {
        const radius = Number(cmd.radius) || 0;
        const extent = Number.isFinite(Number(cmd.extent)) ? Number(cmd.extent) : 360;
        const steps = Math.max(12, Math.ceil(Math.abs(extent) / 5));
        const stepAng = extent / steps;
        const arcLen = (2 * Math.PI * Math.abs(radius) * Math.abs(stepAng)) / 360;
        for (let i = 0; i < steps; i += 1) {
          const fr = (s.angle * Math.PI) / 180;
          moveTurtleTo(s.x + Math.cos(fr) * arcLen, s.y + Math.sin(fr) * arcLen);
          s.angle += radius >= 0 ? stepAng : -stepAng;
        }
        redrawAll();
        return;
      }

      // robot
      if (type === 'robot_init') {
        if (Number(cmd.rows) > 0) s.robotRows = Number(cmd.rows);
        if (Number(cmd.cols) > 0) s.robotCols = Number(cmd.cols);
        if (Number.isFinite(Number(cmd.row))) s.robotRow = Number(cmd.row);
        if (Number.isFinite(Number(cmd.col))) s.robotCol = Number(cmd.col);
        if (cmd.facing) s.robotFacing = String(cmd.facing).toUpperCase()[0];
        if (Array.isArray(cmd.walls)) {
          s.walls = new Set();
          for (const item of cmd.walls) {
            if (Array.isArray(item) && item.length >= 3) {
              s.walls.add(normalizeWall(Number(item[0]), Number(item[1]), item[2]));
            }
          }
        }
        if (Array.isArray(cmd.painted)) {
          s.painted = new Set(cmd.painted.map((p) => (Array.isArray(p) ? `${p[0]},${p[1]}` : String(p))));
        }
        s.mode = 'robot';
        setModeLabel('robot');
        setHint(hintForMode('robot'));
        redrawAll();
        return;
      }
      if (type === 'robot_move') {
        if (Number.isFinite(Number(cmd.row))) s.robotRow = Number(cmd.row);
        if (Number.isFinite(Number(cmd.col))) s.robotCol = Number(cmd.col);
        if (cmd.facing) s.robotFacing = String(cmd.facing).toUpperCase()[0];
        s.robotMsg = cmd.message ? String(cmd.message) : '';
        redrawAll();
        return;
      }
      if (type === 'robot_paint') {
        const r = Number.isFinite(Number(cmd.row)) ? Number(cmd.row) : s.robotRow;
        const c = Number.isFinite(Number(cmd.col)) ? Number(cmd.col) : s.robotCol;
        s.painted.add(`${r},${c}`);
        redrawAll();
        return;
      }
      if (type === 'robot_message') {
        s.robotMsg = cmd.text ? String(cmd.text) : '';
        redrawAll();
        return;
      }

      // game scene objects (canvas top-left origin, +y down)
      if (type === 'scene_clear') {
        s.sceneObjects.clear();
        s.mode = 'game';
        s.showGrid = false;
        setModeLabel('game');
        setHint(hintForMode('game'));
        scheduleRedraw();
        return;
      }
      if (type === 'scene_spawn') {
        const id = String(cmd.id || s.nextShapeId++);
        s.sceneObjects.set(id, {
          id,
          kind: String(cmd.kind || 'circle'),
          x: Number(cmd.x) || 0,
          y: Number(cmd.y) || 0,
          r: Number(cmd.r) || 10,
          w: Number(cmd.w) || 20,
          h: Number(cmd.h) || 20,
          fill: cmd.fill ? String(cmd.fill) : '#38bdf8',
        });
        if (s.mode !== 'game') {
          s.mode = 'game';
          s.showGrid = false;
          setModeLabel('game');
          setHint(hintForMode('game'));
        }
        scheduleRedraw();
        return;
      }
      if (type === 'scene_update') {
        const id = String(cmd.id || '');
        const obj = s.sceneObjects.get(id);
        if (obj) {
          for (const key of ['x', 'y', 'r', 'w', 'h', 'fill']) {
            if (cmd[key] != null) {
              obj[key] = key === 'fill' ? String(cmd[key]) : Number(cmd[key]);
            }
          }
          scheduleRedraw();
        }
        return;
      }
      if (type === 'scene_destroy') {
        s.sceneObjects.delete(String(cmd.id || ''));
        scheduleRedraw();
        return;
      }

      // legacy game shapes
      if (type === 'draw_rect' || type === 'draw_oval' || type === 'draw_line' || type === 'draw_text' || type === 'draw_poly') {
        const id = cmd.id != null ? String(cmd.id) : String(s.nextShapeId++);
        const shape = {
          id,
          type: type.replace('draw_', ''),
          x1: Number(cmd.x1) || 0,
          y1: Number(cmd.y1) || 0,
          x2: Number(cmd.x2) || 0,
          y2: Number(cmd.y2) || 0,
          x: Number(cmd.x) || 0,
          y: Number(cmd.y) || 0,
          text: cmd.text || '',
          font: cmd.font || '14px sans-serif',
          fill: cmd.fill || null,
          outline: cmd.outline || null,
          points: cmd.points || [],
        };
        s.shapes.set(id, shape);
        if (s.mode !== 'game') {
          s.mode = 'game';
          setModeLabel('game');
          setHint(hintForMode('game'));
        }
        redrawAll();
        return id;
      }
      if (type === 'delete') {
        if (cmd.id === 'all' || cmd.id == null) s.shapes.clear();
        else s.shapes.delete(String(cmd.id));
        redrawAll();
      }
    }

    async function processQueue() {
      if (processingRef.current) return;
      processingRef.current = true;
      while (queueRef.current.length) {
        const cmd = queueRef.current.shift();
        handleCommand(cmd);
        const delay = SPEED_MS[state.speed] ?? 12;
        const t = String(cmd?.type || '').toLowerCase();
        const animate = ['forward', 'goto', 'setx', 'sety', 'circle', 'robot_move', 'left', 'right'].includes(t);
        if (animate && delay > 0) await sleep(delay);
      }
      processingRef.current = false;
    }

    const IMMEDIATE_TYPES = new Set([
      'scene_clear', 'scene_spawn', 'scene_update', 'scene_destroy', 'session', 'mode',
    ]);

    function enqueue(detail) {
      const list = Array.isArray(detail) ? detail : (detail ? [detail] : []);
      let needProcess = false;
      for (const c of list) {
        const t = String(c?.type || '').toLowerCase();
        if (IMMEDIATE_TYPES.has(t) || t === '_control') {
          handleCommand(c);
        } else {
          queueRef.current.push(c);
          needProcess = true;
        }
      }
      if (needProcess) processQueue();
    }

    function onEvent(ev) {
      enqueue(ev?.detail);
    }

    function onClick(ev) {
      if (!state.session) return;
      const rect = canvas.getBoundingClientRect();
      const x = ((ev.clientX - rect.left) / rect.width) * state.w;
      const y = ((ev.clientY - rect.top) / rect.height) * state.h;
      emitOut({ type: 'click', x, y });
    }

    clearAll();
    window.addEventListener(EVENT_IN, onEvent);
    // legacy alias
    window.addEventListener(EVENT_IN_LEGACY, onEvent);
    canvas.addEventListener('click', onClick);

    return () => {
      window.removeEventListener(EVENT_IN, onEvent);
      window.removeEventListener(EVENT_IN_LEGACY, onEvent);
      canvas.removeEventListener('click', onClick);
      if (tickTimerRef.current) {
        clearInterval(tickTimerRef.current);
        tickTimerRef.current = null;
      }
      for (const t of afterTimersRef.current.values()) clearTimeout(t);
      afterTimersRef.current.clear();
    };
  }, [height, initialMode, configKey]);

  // Tick / after scheduling via canvas commands from main thread bridge
  useEffect(() => {
    function onControl(ev) {
      const detail = ev?.detail;
      if (!detail || typeof detail !== 'object') return;
      const type = String(detail.type || '').toLowerCase();
      if (type === 'start_tick') {
        const ms = Math.max(16, Number(detail.ms) || 16);
        if (tickTimerRef.current) clearInterval(tickTimerRef.current);
        let last = performance.now();
        tickTimerRef.current = setInterval(() => {
          const now = performance.now();
          let dt = (now - last) / 1000;
          last = now;
          if (dt > 0.1) dt = 0.1;
          if (dt < 0) dt = 1 / 60;
          const event = { type: 'tick', dt };
          const handler = onCanvasEventRef.current;
          if (typeof handler === 'function') handler(event);
          else window.dispatchEvent(new CustomEvent(EVENT_OUT, { detail: event }));
        }, ms);
      } else if (type === 'stop_tick') {
        if (tickTimerRef.current) {
          clearInterval(tickTimerRef.current);
          tickTimerRef.current = null;
        }
      } else if (type === 'after') {
        const id = String(detail.id || '');
        const ms = Math.max(0, Number(detail.ms) || 0);
        const prev = afterTimersRef.current.get(id);
        if (prev) clearTimeout(prev);
        afterTimersRef.current.set(
          id,
          setTimeout(() => {
            afterTimersRef.current.delete(id);
            const event = { type: 'after', id };
            const handler = onCanvasEventRef.current;
            if (typeof handler === 'function') handler(event);
            else window.dispatchEvent(new CustomEvent(EVENT_OUT, { detail: event }));
          }, ms),
        );
      } else if (type === 'cancel_after') {
        const id = String(detail.id || '');
        const prev = afterTimersRef.current.get(id);
        if (prev) clearTimeout(prev);
        afterTimersRef.current.delete(id);
      }
    }
    window.addEventListener('diobuch-canvas-control', onControl);
    return () => window.removeEventListener('diobuch-canvas-control', onControl);
  }, []);

  function handleClear() {
    window.dispatchEvent(new CustomEvent(EVENT_IN, { detail: { type: 'clear' } }));
  }

  const label =
    title
    || (modeLabel === 'robot' ? 'Робот' : modeLabel === 'game' ? 'Холст' : 'Черепаха');

  return (
    <div className="code-canvas-wrap turtle-canvas-wrap">
      <div className="code-canvas-toolbar turtle-canvas-toolbar">
        <span>{label}</span>
        <button type="button" className="btn btn-outline btn-xs" onClick={handleClear}>
          Очистить
        </button>
      </div>
      <canvas ref={canvasRef} className="code-canvas turtle-canvas" style={{ height }} />
      <p className="code-canvas-hint turtle-canvas-hint">{hint}</p>
    </div>
  );
}

function hintForMode(mode) {
  if (mode === 'robot') {
    return (
      <>
        Из Python: <code>import robot</code> затем <code>robot.вправо()</code>, <code>robot.закрасить()</code>.
      </>
    );
  }
  if (mode === 'game') {
    return (
      <>
        Из Python: <code>import game</code> — <code>game.circle</code>, <code>@game.on_click</code>, <code>@game.on_update</code>, <code>game.run()</code>.
      </>
    );
  }
  return (
    <>
      Из Python: <code>import turtle</code> затем <code>turtle.forward(50)</code>, <code>turtle.left(90)</code>.
    </>
  );
}

export { EVENT_IN, EVENT_OUT, hasWall, normalizeWall };
